import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../context/AuthContext';
import DirectGoogleLogin from './DirectGoogleLogin';

// Debug flag for enhanced logging


/**
 * Google Login button component that handles OAuth authentication
 */
const LoginButton = () => {
  const { handleGoogleSignIn, loading } = useAuth();
  const googleButtonRef = useRef(null);
  const [googleClientId, setGoogleClientId] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  
  // Check if we're running on localhost
  const isLocalhost = () => {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
  };
  
  useEffect(() => {
    // Fetch the Google client ID from the backend
    const fetchGoogleClientId = async () => {
      try {
        console.log('[AUTH_DEBUG] Fetching Google Client ID from backend...');
        setConfigLoading(true);
        
        // Log request details
        const startTime = performance.now();
        console.log('[AUTH_DEBUG] Sending request to /api/config endpoint');
        
        const response = await fetch('/api/config');
        const endTime = performance.now();
        console.log(`[AUTH_DEBUG] Config response received in ${(endTime - startTime).toFixed(2)}ms with status: ${response.status}`);
        console.log('[AUTH_DEBUG] Response headers:', Object.fromEntries([...response.headers.entries()]));
        
        if (!response.ok) {
          console.error(`[AUTH_DEBUG] Config fetch failed with status: ${response.status}`);
          return;
        }
        
        const data = await response.json();
        if (data.googleClientId) {
          const clientId = data.googleClientId;
          setGoogleClientId(clientId);
          console.log(`[AUTH_DEBUG] Google Client ID successfully loaded: ${clientId.substring(0, 5)}...${clientId.substring(clientId.length - 5)}`);
          console.log('[AUTH_DEBUG] Client ID length:', clientId.length);
          
          // Validate client ID format
          if (clientId.includes('.apps.googleusercontent.com')) {
            console.log('[AUTH_DEBUG] Client ID appears to be in valid format');
          } else {
            console.warn('[AUTH_DEBUG] Client ID does not match expected format (should end with .apps.googleusercontent.com)');
          }
        } else {
          console.error('[AUTH_DEBUG] Google Client ID not found in backend config');
          console.log('[AUTH_DEBUG] Full config response:', data);
        }
      } catch (error) {
        console.error('[AUTH_DEBUG] Failed to fetch application config:', error);
        console.error('[AUTH_DEBUG] Error stack:', error.stack);
      } finally {
        setConfigLoading(false);
      }
    };
    
    fetchGoogleClientId();
  }, []);

  useEffect(() => {
    // Load the Google API script
    const loadGoogleScript = () => {
      console.log('[AUTH_DEBUG] Loading Google Identity Services script...');
      
      // Check browser environment
      console.log('[AUTH_DEBUG] Browser environment check:');
      console.log('[AUTH_DEBUG] - User Agent:', navigator.userAgent);
      console.log('[AUTH_DEBUG] - Cookies enabled:', navigator.cookieEnabled);
      console.log('[AUTH_DEBUG] - Current origin:', window.location.origin);
      console.log('[AUTH_DEBUG] - Protocol:', window.location.protocol);
      console.log('[AUTH_DEBUG] - Third-party cookies test:', document.cookie ? 'Cookies accessible' : 'Cookies may be blocked');
      
      // Check if script is already loaded
      if (window.google && window.google.accounts && window.google.accounts.id) {
        console.log('[AUTH_DEBUG] Google Identity Services already loaded in window object');
        console.log('[AUTH_DEBUG] Google object structure:', {
          hasGoogle: !!window.google,
          hasAccounts: !!(window.google && window.google.accounts),
          hasId: !!(window.google && window.google.accounts && window.google.accounts.id),
          hasInitialize: !!(window.google && window.google.accounts && window.google.accounts.id && window.google.accounts.id.initialize),
          hasRenderButton: !!(window.google && window.google.accounts && window.google.accounts.id && window.google.accounts.id.renderButton),
          hasPrompt: !!(window.google && window.google.accounts && window.google.accounts.id && window.google.accounts.id.prompt)
        });
        initializeGoogleClient();
        return;
      }
      
      // Check if script is already in the DOM
      const existingScript = document.querySelector('script[src^="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        console.log('[AUTH_DEBUG] Google script already in DOM, waiting for it to load');
        console.log('[AUTH_DEBUG] Existing script attributes:', {
          src: existingScript.src,
          async: existingScript.async,
          defer: existingScript.defer,
          id: existingScript.id,
          status: existingScript.dataset.loaded ? 'loaded' : 'loading'
        });
        
        // Add a small delay to allow the script to load
        const startTime = performance.now();
        const checkScriptLoaded = setInterval(() => {
          const elapsedTime = performance.now() - startTime;
          console.log(`[AUTH_DEBUG] Waiting for Google script to load... (${Math.round(elapsedTime)}ms elapsed)`);
          
          if (window.google && window.google.accounts && window.google.accounts.id) {
            clearInterval(checkScriptLoaded);
            console.log(`[AUTH_DEBUG] Google script loaded after ${Math.round(elapsedTime)}ms`);
            initializeGoogleClient();
          } else if (elapsedTime > 10000) { // 10 seconds timeout
            clearInterval(checkScriptLoaded);
            console.error('[AUTH_DEBUG] Timeout waiting for Google script to load');
            // Try reloading the script
            existingScript.remove();
            setTimeout(loadGoogleScript, 100);
          }
        }, 100);
        return;
      }
      
      // Create script element with detailed logging
      console.log('[AUTH_DEBUG] Creating new Google Identity Services script element');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = 'google-identity-script';
      script.setAttribute('data-load-time', Date.now());
      script.setAttribute('crossorigin', 'anonymous'); // Try with crossorigin attribute
      
      console.log('[AUTH_DEBUG] Script element created with attributes:', {
        src: script.src,
        async: script.async,
        defer: script.defer,
        id: script.id,
        crossorigin: script.getAttribute('crossorigin')
      });
      
      // Add error handling first with detailed logging
      script.onerror = (error) => {
        console.error('[AUTH_DEBUG] Failed to load Google Identity Services script:', error);
        console.error('[AUTH_DEBUG] Error details:', {
          type: error.type,
          bubbles: error.bubbles,
          cancelable: error.cancelable,
          timeStamp: error.timeStamp,
          loadTime: Date.now() - parseInt(script.getAttribute('data-load-time') || '0')
        });
        
        // Network status check
        console.log('[AUTH_DEBUG] Checking network status...');
        if (!navigator.onLine) {
          console.error('[AUTH_DEBUG] Browser is offline, cannot load Google script');
          return;
        }
        
        // Try fallback URL with different options
        console.log('[AUTH_DEBUG] Attempting to load fallback script...');
        const fallbackScript = document.createElement('script');
        fallbackScript.src = 'https://accounts.google.com/gsi/client';
        fallbackScript.async = true;
        fallbackScript.defer = true;
        fallbackScript.id = 'google-identity-script-fallback';
        fallbackScript.setAttribute('data-load-time', Date.now());
        // Try without crossorigin
        
        fallbackScript.onload = () => {
          const loadTime = Date.now() - parseInt(fallbackScript.getAttribute('data-load-time') || '0');
          console.log(`[AUTH_DEBUG] Fallback Google script loaded successfully in ${loadTime}ms`);
          
          // Check if Google object is available
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.log('[AUTH_DEBUG] Google object available after fallback script load');
            initializeGoogleClient();
          } else {
            console.warn('[AUTH_DEBUG] Google object NOT available after fallback script load');
            // Try one more time after a delay
            setTimeout(() => {
              if (window.google && window.google.accounts && window.google.accounts.id) {
                console.log('[AUTH_DEBUG] Google object available after delay');
                initializeGoogleClient();
              } else {
                console.error('[AUTH_DEBUG] Google object still not available after delay');
              }
            }, 1000);
          }
        };
        
        fallbackScript.onerror = (fallbackError) => {
          console.error('[AUTH_DEBUG] Fallback Google script also failed to load:', fallbackError);
          console.error('[AUTH_DEBUG] Fallback error details:', {
            type: fallbackError.type,
            bubbles: fallbackError.bubbles,
            cancelable: fallbackError.cancelable,
            timeStamp: fallbackError.timeStamp,
            loadTime: Date.now() - parseInt(fallbackScript.getAttribute('data-load-time') || '0')
          });
        };
        
        document.body.appendChild(fallbackScript);
        console.log('[AUTH_DEBUG] Fallback script added to DOM');
      };
      
      // Set onload after error handler with detailed logging
      script.onload = () => {
        const loadTime = Date.now() - parseInt(script.getAttribute('data-load-time') || '0');
        console.log(`[AUTH_DEBUG] Google Identity Services script loaded successfully in ${loadTime}ms`);
        
        // Check if Google object is available immediately
        if (window.google && window.google.accounts && window.google.accounts.id) {
          console.log('[AUTH_DEBUG] Google object available immediately after script load');
          initializeGoogleClient();
          return;
        }
        
        console.log('[AUTH_DEBUG] Google object not immediately available, waiting...');
        // Small delay to ensure Google's code is fully initialized
        setTimeout(() => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.log('[AUTH_DEBUG] Google object available after short delay');
            initializeGoogleClient();
          } else {
            console.warn('[AUTH_DEBUG] Google object not available after short delay, retrying...');
            // Try one more time after a longer delay
            setTimeout(() => {
              if (window.google && window.google.accounts && window.google.accounts.id) {
                console.log('[AUTH_DEBUG] Google object available after longer delay');
                initializeGoogleClient();
              } else {
                console.error('[AUTH_DEBUG] Google object still not available after longer delay');
                // Final attempt - reload the script
                script.remove();
                console.log('[AUTH_DEBUG] Removed failed script, will try again');
                setTimeout(loadGoogleScript, 500);
              }
            }, 1000);
          }
        }, 100);
      };
      
      // Add to DOM with timing
      const beforeInsert = Date.now();
      document.body.appendChild(script);
      console.log(`[AUTH_DEBUG] Google script added to DOM at ${new Date().toISOString()}, took ${Date.now() - beforeInsert}ms`);
    };
    
    // Initialize Google Identity Services client with enhanced logging
    const initializeGoogleClient = () => {
      console.log('[AUTH_DEBUG] Initializing Google Identity Services client');
      
      // Check prerequisites with detailed logging
      const hasGoogle = !!window.google;
      const hasAccounts = !!(window.google && window.google.accounts);
      const hasId = !!(window.google && window.google.accounts && window.google.accounts.id);
      const hasClientId = !!googleClientId;
      
      console.log('[AUTH_DEBUG] Initialization prerequisites:', {
        hasGoogle,
        hasAccounts,
        hasId,
        hasClientId,
        googleClientIdLength: googleClientId ? googleClientId.length : 0,
        actualClientId: googleClientId ? `${googleClientId.substring(0, 5)}...${googleClientId.substring(googleClientId.length - 5)}` : 'EMPTY'
      });
      
      // Critical: Ensure we have a valid client ID before proceeding
      if (!googleClientId || googleClientId.trim() === '') {
        console.error('[AUTH_DEBUG] Cannot initialize: googleClientId is empty or undefined');
        console.error('[AUTH_DEBUG] Current googleClientId value:', googleClientId);
        return;
      }
      
      if (hasGoogle && hasAccounts && hasId && hasClientId) {
        try {
          // Determine the redirect URI based on the current environment with detailed logging
          const hostname = window.location.hostname;
          const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
          
          // For local testing, try both http and https for localhost
          const protocol = window.location.protocol;
          const port = window.location.port || (protocol === 'https:' ? '443' : '80');
          
          console.log('[AUTH_DEBUG] Environment detection:', {
            hostname,
            isLocalhost,
            protocol,
            port,
            fullOrigin: window.location.origin
          });
          
          // Always use popup mode for localhost to avoid redirect issues
          const uxMode = 'popup';
          
          console.log(`[AUTH_DEBUG] Using ${uxMode} mode for Google OAuth`);
          
          // Log initialization options for debugging
          const initOptions = {
            client_id: `${googleClientId.substring(0, 5)}...${googleClientId.substring(googleClientId.length - 5)}`,
            ux_mode: uxMode,
            auto_select: false,
            cancel_on_tap_outside: true,
            prompt_parent_id: null,
            use_fedcm_for_prompt: false
          };
          
          console.log('[AUTH_DEBUG] Initializing with options:', initOptions);
          
          // Actual initialization with full options
          const startTime = performance.now();
          
          // Final validation before calling Google API
          console.log('[AUTH_DEBUG] About to call google.accounts.id.initialize with client_id:', 
            googleClientId ? `${googleClientId.substring(0, 10)}...${googleClientId.substring(googleClientId.length - 10)}` : 'UNDEFINED');
          
          if (!googleClientId) {
            console.error('[AUTH_DEBUG] CRITICAL: googleClientId is still undefined at initialization time!');
            return;
          }
          
          // Test basic Google API availability first
          console.log('[AUTH_DEBUG] Testing Google API availability...');
          console.log('[AUTH_DEBUG] window.google:', !!window.google);
          console.log('[AUTH_DEBUG] window.google.accounts:', !!window.google?.accounts);
          console.log('[AUTH_DEBUG] window.google.accounts.id:', !!window.google?.accounts?.id);
          console.log('[AUTH_DEBUG] window.google.accounts.id.initialize:', typeof window.google?.accounts?.id?.initialize);
          
          // Try minimal initialization first
          console.log('[AUTH_DEBUG] Attempting minimal Google Identity Services initialization...');
          
          try {
            window.google.accounts.id.initialize({
              client_id: googleClientId,
              callback: handleCredentialResponse
            });
            console.log('[AUTH_DEBUG] Minimal initialization successful!');
          } catch (minimalError) {
            console.error('[AUTH_DEBUG] Minimal initialization failed:', minimalError);
            console.error('[AUTH_DEBUG] Error details:', {
              name: minimalError.name,
              message: minimalError.message,
              stack: minimalError.stack
            });
            
            // Try with additional debugging
            console.log('[AUTH_DEBUG] Client ID details:', {
              length: googleClientId.length,
              starts_with: googleClientId.substring(0, 10),
              ends_with: googleClientId.substring(googleClientId.length - 10),
              contains_apps_googleusercontent: googleClientId.includes('apps.googleusercontent.com')
            });
            
            return; // Don't continue if minimal init fails
          }
          
          const endTime = performance.now();
          console.log(`[AUTH_DEBUG] Google Identity Services initialized successfully (${uxMode} mode) in ${(endTime - startTime).toFixed(2)}ms`);
          
          // Extra logging: check API availability
          console.log('[AUTH_DEBUG] Google API feature detection:', {
            hasFederatedCredential: !!window.FederatedCredential,
            hasPromptStatus: !!(window.google?.accounts?.id?.getPromptStatus),
            hasCancel: !!(window.google?.accounts?.id?.cancel),
            hasDisableAutoSelect: !!(window.google?.accounts?.id?.disableAutoSelect),
            hasRenderButton: !!(window.google?.accounts?.id?.renderButton),
            hasRevoke: !!(window.google?.accounts?.id?.revoke)
          });
          
          // Test if we can access the prompt method
          try {
            if (window.google?.accounts?.id?.prompt) {
              console.log('[AUTH_DEBUG] prompt() method is available');
            } else {
              console.warn('[AUTH_DEBUG] prompt() method is NOT available');
            }
          } catch (promptError) {
            console.error('[AUTH_DEBUG] Error checking prompt() availability:', promptError);
          }
        
        } catch (error) {
          console.error('[AUTH_DEBUG] Error initializing Google Identity Services:', error);
          console.error('[AUTH_DEBUG] Error stack:', error.stack);
        }
      } else {
        console.error('[AUTH_DEBUG] Cannot initialize Google client - missing requirements:', {
          'google object available': hasGoogle,
          'google.accounts available': hasAccounts,
          'google.accounts.id available': hasId,
          'client ID available': hasClientId,
          'window.google structure': typeof window.google
        });
      }
    };
    
    // Handle credential response from Google with enhanced logging
    const handleCredentialResponse = (response) => {
      console.log('[AUTH_DEBUG] Received credential response from Google');
      
      // Log response details (safely)
      const responseKeys = Object.keys(response || {});
      console.log('[AUTH_DEBUG] Response contains keys:', responseKeys);
      
      if (response.credential) {
        const token = response.credential;
        const tokenLength = token.length;
        
        // Log token details without exposing the full token
        console.log(`[AUTH_DEBUG] Google credential received (${tokenLength} chars): ${token.substring(0, 10)}...${token.substring(tokenLength - 5)}`);
        
        // Check token format (JWT typically has 3 parts separated by dots)
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          console.log('[AUTH_DEBUG] Token appears to be in valid JWT format with 3 parts');
          
          try {
            // Try to decode the JWT header (first part) to verify format
            // This is safe as it only contains algorithm and token type info
            const headerJson = atob(tokenParts[0]);
            const header = JSON.parse(headerJson);
            console.log('[AUTH_DEBUG] Token header:', header);
          } catch (e) {
            console.warn('[AUTH_DEBUG] Could not decode token header:', e);
          }
        } else {
          console.warn(`[AUTH_DEBUG] Token does not appear to be in standard JWT format (has ${tokenParts.length} parts instead of 3)`);
        }
        
        console.log('[AUTH_DEBUG] Sending token to backend for verification');
        // Send the token to our backend for verification
        handleGoogleSignIn(token);
        console.log(`[AUTH_DEBUG] handleGoogleSignIn called at ${new Date().toISOString()}`);
      } else {
        console.error('[AUTH_DEBUG] Google response missing credential', response);
        
        // Check for error information
        if (response.error) {
          console.error(`[AUTH_DEBUG] Google auth error: ${response.error}`);
          if (response.error_description) {
            console.error(`[AUTH_DEBUG] Error description: ${response.error_description}`);
          }
        }
      }
    };
    
    // Only load the script once we have the client ID
    if (googleClientId && googleClientId.trim() !== '') {
      console.log('[AUTH_DEBUG] Loading Google script with client ID:', 
        `${googleClientId.substring(0, 5)}...${googleClientId.substring(googleClientId.length - 5)}`);
      loadGoogleScript();
    } else {
      console.log('[AUTH_DEBUG] Waiting for googleClientId to be loaded before loading Google script');
      console.log('[AUTH_DEBUG] Current googleClientId state:', googleClientId);
    }
    
    // Cleanup function
    return () => {
      // Revoke all callbacks and reset the singleton OAuth client
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [handleGoogleSignIn, googleClientId]);
  
  // Handle the button click with enhanced logging
  const handleLogin = () => {
    console.log('[AUTH_DEBUG] Login button clicked at', new Date().toISOString());
    
    // Check browser environment
    const cookiesEnabled = navigator.cookieEnabled;
    const hasLocalStorage = !!window.localStorage;
    const hasSessionStorage = !!window.sessionStorage;
    
    console.log('[AUTH_DEBUG] Browser capabilities:', {
      cookiesEnabled,
      hasLocalStorage,
      hasSessionStorage,
      userAgent: navigator.userAgent,
      language: navigator.language,
      doNotTrack: navigator.doNotTrack,
      onLine: navigator.onLine
    });
    
    // Check if Google Identity Services is loaded
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      console.error('[AUTH_DEBUG] Google Identity Services not loaded, attempting to reload');
      console.error('[AUTH_DEBUG] Missing components:', {
        hasGoogle: !window.google,
        hasAccounts: !window.google?.accounts,
        hasId: !window.google?.accounts?.id
      });
      // Google script not available, cannot proceed
      console.error('[AUTH_DEBUG] Google Identity Services script not available');
      return;
    }
    
    console.log('[AUTH_DEBUG] Initiating Google sign-in');
    try {
      // Check if we need to re-initialize
      if (!window.google.accounts.id.initialize) {
        console.warn('[AUTH_DEBUG] Google Identity Services not properly initialized');
        return;
      }
      
      // Use the renderButton method instead of prompt for better compatibility
      if (!window.googleSignInButtonInitialized) {
        console.log('[AUTH_DEBUG] Rendering Google Sign-In button');
        try {
          // Log button container details
          const buttonContainer = googleButtonRef.current;
          console.log('[AUTH_DEBUG] Button container:', {
            exists: !!buttonContainer,
            width: buttonContainer?.offsetWidth,
            height: buttonContainer?.offsetHeight,
            visible: buttonContainer ? buttonContainer.offsetParent !== null : false
          });
          
          // Configure button options with detailed logging
          const buttonOptions = {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            width: buttonContainer?.offsetWidth,
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
          };
          
          console.log('[AUTH_DEBUG] Rendering button with options:', buttonOptions);
          
          // Render the button with timing
          const renderStart = performance.now();
          window.google.accounts.id.renderButton(buttonContainer, buttonOptions);
          console.log(`[AUTH_DEBUG] Button render call completed in ${(performance.now() - renderStart).toFixed(2)}ms`);
          
          // Prompt for sign-in with timing
          console.log('[AUTH_DEBUG] Calling prompt() to initiate sign-in flow');
          const promptStart = performance.now();
          window.google.accounts.id.prompt((notification) => {
            console.log(`[AUTH_DEBUG] Prompt notification received after ${(performance.now() - promptStart).toFixed(2)}ms:`, notification);
          });
          
          window.googleSignInButtonInitialized = true;
          console.log('[AUTH_DEBUG] Google Sign-In button rendered successfully');
        } catch (renderError) {
          console.error('[AUTH_DEBUG] Error rendering Google button:', renderError);
          console.error('[AUTH_DEBUG] Error stack:', renderError.stack);
          
          // Try to reinitialize and retry once
          console.log('[AUTH_DEBUG] Cannot reinitialize - Google client not available');
          
          setTimeout(() => {
            console.log('[AUTH_DEBUG] Delayed prompt attempt after reinitialization');
            if (window.google && window.google.accounts && window.google.accounts.id) {
              try {
                window.google.accounts.id.prompt();
                console.log('[AUTH_DEBUG] Delayed prompt call succeeded');
              } catch (promptError) {
                console.error('[AUTH_DEBUG] Delayed prompt call failed:', promptError);
              }
            } else {
              console.error('[AUTH_DEBUG] Google object still not available for delayed prompt');
            }
          }, 1000); // Increased delay for better recovery chance
        }
      } else {
        // If button is already initialized, just trigger the prompt
        console.log('[AUTH_DEBUG] Triggering existing Google Sign-In prompt');
        const promptStart = performance.now();
        window.google.accounts.id.prompt((notification) => {
          console.log(`[AUTH_DEBUG] Prompt notification received after ${(performance.now() - promptStart).toFixed(2)}ms:`, notification);
        });
      }
    } catch (error) {
      console.error('[AUTH_DEBUG] Error during Google sign-in:', error);
      console.error('[AUTH_DEBUG] Error stack:', error.stack);
    }
  };
  
  // Conditionally render different buttons based on environment
  if (isLocalhost()) {
    // Show DirectGoogleLogin for localhost (redirect-based OAuth)
    return (
      <DirectGoogleLogin
        onSuccess={(user) => {
          console.log('[AUTH_DEBUG] Direct OAuth login successful:', user);
        }}
        onError={(error) => {
          console.error('[AUTH_DEBUG] Direct OAuth login failed:', error);
        }}
      />
    );
  } else {
    // Show regular Google Identity Services button for web (popup-based)
    return (
      <Button
        ref={googleButtonRef}
        variant="outlined"
        sx={{
          color: '#1976d2',
          backgroundColor: 'white',
          borderColor: '#1976d2',
          fontWeight: 'bold',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#1976d2'
          }
        }}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
        onClick={handleLogin}
        disabled={loading || configLoading || !googleClientId}
      >
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </Button>
    );
  }
};

export default LoginButton;
